import React, { CSSProperties } from 'react'

import { cx } from '@emotion/css'

import {
  Box,
  styled,
  Tooltip as MaterialTooltip,
  Typography,
  useTheme,
} from '@mui/material'

import { getClassName } from '../../utils'

export interface ITooltip {
  /**
   * The tooltip will be added relative to this element.
   */
  children?: React.ReactElement

  /**
   * The tooltip title.
   */
  title: string

  /**
   * The tooltip placement.
   */
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top'

  style?: CSSProperties | string
  titleStyle?: CSSProperties | string
  containerStyle?: CSSProperties | string
}

const StyledContainer = styled(Box, { name: 'StyledContainer' })(() => ({
  display: 'inline',
}))

const StyledTypography = styled(Typography, { name: 'StyledTypography' })(
  ({ theme }) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: '16px',
    color: theme.palette.white.primary,
  })
)

export const Tooltip = React.forwardRef<HTMLDivElement, ITooltip>(
  ({ children, placement, title, style, titleStyle, containerStyle }, ref) => {
    const theme = useTheme()

    const defaultStyle = `
      box-sizing: border-box;
      height: 24px;
      background-color: #323232;
      padding: ${theme.spacing(0.5, 1)};
      border-radius: 2px;
    `

    return children ? (
      <MaterialTooltip
        classes={{
          tooltip: cx(getClassName(style ?? defaultStyle)),
        }}
        ref={ref}
        placement={placement}
        title={
          <StyledTypography className={cx(getClassName(titleStyle))}>
            {title}
          </StyledTypography>
        }
      >
        <StyledContainer className={cx(getClassName(containerStyle))}>
          {children}
        </StyledContainer>
      </MaterialTooltip>
    ) : null
  }
)

Tooltip.displayName = 'Tooltip'
